import { Terrace, DetailedOpeningHours, TerraceSubmission } from '@/types';

export class TerraceDataTransformer {
  /**
   * Transform raw database terrace data to Terrace interface
   */
  static transformDatabaseToTerrace(data: any): Terrace {
    // Parse detailed_opening_hours from JSON to DetailedOpeningHours
    let detailedOpeningHours: DetailedOpeningHours | undefined;
    if (data.detailed_opening_hours) {
      try {
        detailedOpeningHours = typeof data.detailed_opening_hours === 'string' 
          ? JSON.parse(data.detailed_opening_hours)
          : data.detailed_opening_hours as unknown as DetailedOpeningHours;
      } catch (e) {
        console.warn("Failed to parse detailed opening hours:", e);
      }
    }

    const transformedTerrace: Terrace = {
      id: data.id,
      name: data.name,
      address: data.address,
      lat: data.lat,
      lng: data.lng,
      sunStatus: data.sun_status as 'sunny' | 'soon' | 'not-sunny',
      openingHours: data.opening_hours || '',
      detailed_opening_hours: detailedOpeningHours,
      orientation: data.orientation || '',
      hasFood: data.has_food,
      petFriendly: data.pet_friendly,
      servesAlcohol: data.serves_alcohol,
      terraceDescription: data.terrace_description,
      website: data.website,
      phone: data.phone,
      type: data.type,
      submitted_by: data.submitted_by,
      slug: data.slug,
      sun_status_last_updated: data.sun_status_last_updated,
      original_maps_url: data.original_maps_url,
      photo_url: data.photo_url || data.image_url
    };
    
    return transformedTerrace;
  }

  /**
   * Transform submission data to partial terrace data
   */
  static transformSubmissionToTerrace(submission: TerraceSubmission): Partial<Terrace> {
    return {
      name: submission.name,
      type: submission.type,
      address: submission.address,
      lat: submission.lat,
      lng: submission.lng,
      sunStatus: submission.sunStatus,
      openingHours: submission.openingHours || '',
      detailed_opening_hours: typeof submission.detailed_opening_hours === 'string' 
        ? JSON.parse(submission.detailed_opening_hours) 
        : submission.detailed_opening_hours,
      orientation: submission.orientation || 'unknown',
      hasFood: submission.hasFood,
      petFriendly: submission.petFriendly,
      servesAlcohol: submission.servesAlcohol,
      terraceDescription: submission.terraceDescription,
      website: submission.website,
      phone: submission.phone,
      submitted_by: submission.submitted_by,
      photo_url: submission.photo_url || submission.imageUrl,
    };
  }

  /**
   * Normalize business features to ensure alcohol detection from wine/beer
   */
  static normalizeBusinessFeatures(features: {
    hasFood?: boolean;
    servesAlcohol?: boolean;
    servesWine?: boolean;
    servesBeer?: boolean;
    petFriendly?: boolean;
    wheelchairAccessible?: boolean;
    allowsDogs?: boolean;
  }) {
    return {
      ...features,
      servesAlcohol: features.servesAlcohol || features.servesWine || features.servesBeer || false
    };
  }

  /**
   * Format opening hours for display
   */
  static formatOpeningHours(detailedHours?: DetailedOpeningHours): string {
    if (!detailedHours) return '';
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const openDays = days.filter(day => detailedHours[day as keyof DetailedOpeningHours]?.isOpen);
    
    if (openDays.length === 0) return 'Closed';
    if (openDays.length === 7) return 'Open daily';
    
    return `Open ${openDays.length} days a week`;
  }

  /**
   * Generate URL-friendly slug from name
   */
  static generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[åä]/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  /**
   * Batch transform array of database terraces
   */
  static transformTerracesArray(data: any[]): Terrace[] {
    return data.map(this.transformDatabaseToTerrace);
  }
}